import React, { useState, useRef } from 'react';

import { SpinMachine as BLSpinMachine } from './../../../utilities/BL/SlotMachine'


/**
 * COMPONENTE: SLOT MACHINE FAN (CONTROL REMOTO TÁCTIL)
 * Diseño de casino inmersivo con física de resorte y captura de leads.
 */
const SlotMachine = ({ screenId: propScreenId }) => {
    // MAGIA AQUÍ: Leemos el ID directamente de la URL (?screenId=2)
    // Si no hay nada en la URL, usamos el prop, y si no hay prop, usamos 1 por defecto.
    const queryParams = new URLSearchParams(window.location.search);
    const screenId = parseInt(queryParams.get('screenId') || propScreenId || 1, 10);

    // --- ESTADOS DE FLUJO ---
    const [isSpinning, setIsSpinning] = useState(false);
    const [toast, setToast] = useState('');
    
    // --- PROTOTIPO: SWITCH DE LEADS ---
    // Cambia esto a "false" si quieres ir directo a la palanca
    const [requireLeads, setRequireLeads] = useState(false); 
    const [hasSubmittedLeads, setHasSubmittedLeads] = useState(false);
    
    const [playerData, setPlayerData] = useState({ name: '', contact: '' });

    // --- ESTADOS DE LA PALANCA (FÍSICA) ---
    const [pullDistance, setPullDistance] = useState(0);
    const maxPull = 180; // Pixeles máximos que baja la palanca
    const triggerPoint = 120; // Pixeles necesarios para disparar el tiro
    
    const startY = useRef(0);
    const isDragging = useRef(false);

    // Configuración que luego vendrá de la BD
    const defaultConfig = {
        winProbability: 30, // 30%
        baseTimeMs: 2200,
        celebrationTimeMs: 6000,
        symbols: ['1.png', '2.png', '3.png', '4.png', '5.png', '6.png']
    };

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    // -------------------------------------------------------------------------
    // LÓGICA DEL FORMULARIO DE LEADS
    // -------------------------------------------------------------------------
    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (!playerData.name.trim()) {
            showToast('Por favor, ingresa tu nombre');
            return;
        }
        setHasSubmittedLeads(true);
        showToast(`¡Listo ${playerData.name}! Tira de la palanca.`);
    };

    // -------------------------------------------------------------------------
    // LÓGICA FÍSICA DE LA PALANCA (TOUCH EVENTS)
    // -------------------------------------------------------------------------
    const handleTouchStart = (e) => {
        if (isSpinning) return;
        isDragging.current = true;
        // Soporte para touch (celular) o mouse (PC)
        startY.current = e.touches ? e.touches[0].clientY : e.clientY; 
        setPullDistance(0);
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current || isSpinning) return;
        
        const currentY = e.touches ? e.touches[0].clientY : e.clientY;
        const diffY = currentY - startY.current;

        // Solo permitimos arrastrar hacia ABAJO (valores positivos), con un tope máximo
        if (diffY > 0 && diffY <= maxPull) {
            setPullDistance(diffY);
        } else if (diffY > maxPull) {
            setPullDistance(maxPull);
        }
    };

    const handleTouchEnd = () => {
        if (!isDragging.current || isSpinning) return;
        isDragging.current = false;

        // Si pasó el punto de quiebre, disparamos. Si no, lo devolvemos a cero.
        if (pullDistance >= triggerPoint) {
            setPullDistance(maxPull); // Lo mandamos hasta abajo visualmente
            executeSpin();
        } else {
            setPullDistance(0); // Regresa como resorte
        }
    };

    // -------------------------------------------------------------------------
    // LÓGICA DE TIRO (API CALL REAL)
    // -------------------------------------------------------------------------
    const executeSpin = async () => {
        setIsSpinning(true);
        showToast('¡Suerte!');

        try {
            // El celular ahora es "tonto". Solo manda quién tira y de qué pantalla.
            // Nota: Usamos targetScreenId como lo espera tu DTO en C#
            const requestBody = {
                targetScreenId: parseInt(screenId, 10) || 0,
                playerName: playerData.name || "Aficionado Anónimo",
                playerContact: playerData.contact || "" 
            };

            // Llamamos a tu Business Layer en lugar del fetch directo
            const result = await BLSpinMachine(requestBody);

            if (result.success) {
                // Como ya no tenemos los tiempos locales, usaremos un tiempo fijo seguro 
                // para bloquear la palanca del celular (ej. 8 segundos).
                // En una versión más avanzada, el backend podría devolverte cuánto durará la animación.
                const lockTime = 8000; 
                
                setTimeout(() => {
                    setIsSpinning(false);
                    setPullDistance(0); // Regresamos la palanca arriba
                    
                    if (requireLeads) {
                        setPlayerData({ name: '', contact: '' });
                        setHasSubmittedLeads(false);
                    } else {
                        showToast('¡Listo para el siguiente turno!');
                    }
                }, lockTime);

            } else {
                showToast(`❌ Error: ${result.message || 'Fallo al tirar'}`);
                setIsSpinning(false);
                setPullDistance(0);
            }

        } catch (error) {
            console.error("Error al tirar:", error);
            showToast('❌ Error de conexión');
            setIsSpinning(false);
            setPullDistance(0);
        }
    };

    // -------------------------------------------------------------------------
    // RENDERIZADO VISUAL
    // -------------------------------------------------------------------------
    
    // VISTA 1: FORMULARIO DE LEADS
    if (requireLeads && !hasSubmittedLeads) {
        return (
            <div className="slot-fan-container slot-fan-leads">
                <div className="casino-card text-center">
                    <i className="bi bi-person-vcard text-warning display-1 mb-3"></i>
                    <h2 className="text-white fw-bold text-uppercase mb-2">¡Participa y Gana!</h2>
                    <p className="text-white-50 mb-4">Regístrate rápido para probar tu suerte en la máquina.</p>
                    
                    <form onSubmit={handleFormSubmit}>
                        <input 
                            type="text" 
                            className="form-control form-control-lg mb-3 bg-dark text-white border-warning" 
                            placeholder="Tu Nombre" 
                            value={playerData.name}
                            onChange={(e) => setPlayerData({...playerData, name: e.target.value})}
                            required
                        />
                        <input 
                            type="email" 
                            className="form-control form-control-lg mb-4 bg-dark text-white border-warning" 
                            placeholder="Tu Correo (Opcional)" 
                            value={playerData.contact}
                            onChange={(e) => setPlayerData({...playerData, contact: e.target.value})}
                        />
                        <button type="submit" className="btn btn-warning btn-lg w-100 fw-bold fs-4 text-dark text-uppercase">
                            Jugar Ahora <i className="bi bi-play-circle-fill"></i>
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // VISTA 2: LA PALANCA DE CASINO
    return (
        <>
            <div className='SelfieFan__Screen__QR__Background'>
                <img src='/img/games/runtogoal/background-1-blurred.png' className='SelfieFan__Screen__QR__Background__Video' />
            </div>

            <div className="slot-fan-container"> {/* style={{ color: '#000' }} */}
                <header className="slot-fan-header">
                    <div className="px-3 pt-3">
                        {/* <button type='button' className='btn btn-outline-light btn-sm rounded-pill'>
                            <i className="bi bi-box-arrow-right"></i> Salir
                        </button> */}
                        <img src="/img/page/xolos-five.png" alt="Logo Xolos Five" className='slot-fan-logo' />
                        <p className="d-block small mb-0 text-center">Jugando en pantalla #{screenId}</p>
                    </div>
                    
                    <h3 className="text-center text-white mt-4 fw-bold text-uppercase" style={{letterSpacing: '2px'}}>
                        {playerData.name ? `¡Vamos ${playerData.name}!` : ''}
                    </h3>
                    {/* <p className={`status-indicator ${isSpinning ? 'status-spinning' : 'status-ready'}`}>
                        {isSpinning ? 'Girando...' : '¡Listo!'}
                    </p> */}
                </header>

                <div className='slot-fan-lever-area'>
                    <p className={`status-indicator ${isSpinning ? 'status-spinning' : 'status-ready'}`}>
                        {isSpinning ? '¡Suerte!' : '¡Listo!'}
                    </p>

                    <p className='mb-4 text-center w-100'>
                        {isSpinning ? 'La máquina está girando...' : 'Desliza la palanca hasta abajo'}
                    </p>
                    
                    {/* LA RANURA DE LA PALANCA */}
                    <div className="lever-track">
                        {/* LA PALANCA EN SÍ */}
                        <div 
                            className={`lever-handle ${!isDragging.current && !isSpinning ? 'lever-spring' : ''}`}
                            style={{ transform: `translateY(${pullDistance}px)` }}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onMouseDown={handleTouchStart}
                            onMouseMove={handleTouchMove}
                            onMouseUp={handleTouchEnd}
                            onMouseLeave={handleTouchEnd}
                        >
                            <div className="lever-rod-metal"></div>
                            <div className="lever-ball-red">
                                <span><i className='bi bi-chevron-double-down'></i></span>
                            </div>
                        </div>
                    </div>
                </div>

                {toast && <div className="slot-fan-toast">{toast}</div>}

                {/* <div className='slot-fan-footer'>
                    <p>Hecho con ♥ por Five Digital Minds | 2026</p>
                </div> */}

                <p className='text-center FooterCredits'>Hecho con ♥ por Five Digital Minds | 2026</p> {/* style={{ color: '#000000', backgroundColor: 'rgba(255,255,255,0.85)', padding: '0 16px', borderRadius: '16px' }} */}
            </div>
        </>
    );
};

export { SlotMachine };